import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Paper, Grid, CircularProgress } from "@mui/material";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import AssignmentsTable from "../components/AssignmentsTable";
import ApplicationAssignmentsSummary from "../components/ApplicationAssignmentsSummary";
import useFetchApplicationAssignments from "../hooks/useFetchApplicationAssignments";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import ConfirmationModal from "../components/Modal/ConfirmationModal";
import useReactivateAssignappQuestionnaire from "../hooks/useReactivateAssignappQuestionnaire";

const mapApiRowToTableRow = (row) => ({
  id: row.id,
  applicationId: row.application_id,
  assignappId: row.assignapp_id,
  userId: row.user_id,
  userName: row.user_name,
  userEmail: row.user_email,
  assignStatus: row.assign_status,
  assignStatusLabel: row.assign_status_label,
  assignActivationWindowStart: row.assign_activation_window_start,
  assignActivationWindowEnd: row.assign_activation_window_end,
  questionnaireId: row.questionnaire_id,
  questionnaireTitle: row.questionnaire_title,
  questionnaireExternalId: row.questionnaire_external_id,
  questionnaireType: row.questionnaire_type,
  questionnaireTypeLabel: row.questionnaire_type_label,
  questionnaireStatus: row.questionnaire_status,
  questionnaireStatusLabel: row.questionnaire_status_label,
  questionnaireContext: row.questionnaire_context,
  aqExternalId: row.aq_external_id,
});

const emptyFilters = {
  user: "",
  assignStatus: "",
  windowFrom: "",
  windowTo: "",
  questionnaireType: "",
  questionnaireStatus: "",
  questionnaireSearch: "",
  context: "",
  aqExternalId: "",
};

const ApplicationAssignmentsPage = () => {
  const { appId } = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [draftFilters, setDraftFilters] = useState(emptyFilters);

  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState("desc");

  const [appliedFilters, setAppliedFilters] = useState(draftFilters);

  const { data, error, loading, refetch } = useFetchApplicationAssignments(appId, {
    page,
    perPage,
    filters: appliedFilters,
    sortBy,
    sortDir,
  });

  const summary = data?.summary || null;

  const meta = data?.meta || {
    total_count: 0,
    total_pages: 0,
    page: page + 1,
    per_page: perPage,
  };

  const [errorOpen, setErrorOpen] = useState(false);

  useEffect(() => {
    if (error) setErrorOpen(true);
  }, [error]);

  const application = data?.application || null;

  const assignments = useMemo(() => (data?.assignments || []).map(mapApiRowToTableRow), [data]);

  const hasData = !!application;

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(0);
  };

  const clearFilters = () => {
    const draftIsEmpty = !hasAnyFilter(draftFilters);
    const appliedIsEmpty = !hasAnyFilter(appliedFilters);

    if (draftIsEmpty && appliedIsEmpty) return;

    setDraftFilters(emptyFilters);

    // Solo actualiza si había filtros aplicados
    if (!appliedIsEmpty) {
      setAppliedFilters(emptyFilters);
      setPage(0);
    }
  };

  const onFilterChange = (field, value) => {
    setDraftFilters((prev) => ({ ...prev, [field]: value }));
  };

  const hasAnyFilter = (filters) => Object.values(filters).some((value) => String(value ?? "").trim() !== "");

  // Reactivación de cuestionarios
  const { reactivate } = useReactivateAssignappQuestionnaire();
  const [pendingReactivateRow, setPendingReactivateRow] = useState(null);
  const [reactivateErrorOpen, setReactivateErrorOpen] = useState(false);
  const [reactivateErrorMessage, setReactivateErrorMessage] = useState("");

  const handleReactivate = (row) => {
    setPendingReactivateRow(row);
  };

  const handleConfirmReactivate = async () => {
    const response = await reactivate(pendingReactivateRow.id);

    setPendingReactivateRow(null);

    if (response?.error) {
      setReactivateErrorMessage(response.error.message || "Error al reactivar el cuestionario");
      setReactivateErrorOpen(true);
    } else {
      refetch();
    }
  };

  return (
    <Box sx={{ minWidth: "100%", p: 5, overflowX: "auto" }}>
      <Box
        sx={{
          maxWidth: 1600,
          mx: "auto",
          p: { xs: 2, md: 4 },
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        {/* Encabezado */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#333333" }}>
            {hasData ? `Asignaciones de la aplicación: ${application.name}` : "Asignaciones de la aplicación"}
          </Typography>

          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate("/aplicaciones")} sx={{ textTransform: "none" }}>
            Volver a aplicaciones
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && !hasData && (
          <Typography color="error" variant="body2">
            {error.message || "Error al cargar la información de la aplicación."}
          </Typography>
        )}

        {!loading && hasData && (
          <>
            {/* Información de la aplicación */}
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                p: 2.5,
                bgcolor: "#ffffff",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <Box component="span" fontWeight={600}>
                      ID de aplicación:
                    </Box>{" "}
                    {application.id}
                  </Typography>

                  <Typography variant="body2">
                    <Box component="span" fontWeight={600}>
                      Nombre:
                    </Box>{" "}
                    {application.name}
                  </Typography>
                </Grid>

                {/* Fechas */}
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <Box component="span" fontWeight={600}>
                      Fecha mínima:
                    </Box>{" "}
                    {application.date_min}
                  </Typography>

                  <Typography variant="body2">
                    <Box component="span" fontWeight={600}>
                      Fecha máxima:
                    </Box>{" "}
                    {application.date_max}
                  </Typography>
                </Grid>

                {/* Descripción */}
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Descripción:
                  </Typography>

                  <Box
                    mt={1}
                    p={1.5}
                    sx={{
                      bgcolor: "#fafafa",
                      borderRadius: 1,
                      maxHeight: 200,
                      overflowY: "auto",
                      fontSize: 14,
                      color: "text.secondary",
                    }}
                  >
                    {application.description || "Sin descripción"}
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Resumen general de las asignaciones */}
            {summary && <ApplicationAssignmentsSummary summary={summary} appId={appId} />}

            {/* Tabla de asignaciones */}
            <AssignmentsTable
              rows={assignments}
              totalCount={meta.total_count}
              page={page}
              rowsPerPage={perPage}
              onPageChange={(newPage) => setPage(newPage)}
              onRowsPerPageChange={(newPerPage) => {
                setPerPage(newPerPage);
                setPage(0);
              }}
              filters={draftFilters}
              onFilterChange={onFilterChange}
              onApplyFilters={applyFilters}
              onClearFilters={clearFilters}
              sortBy={sortBy}
              sortDir={sortDir}
              onSortChange={(field, dir) => {
                setSortBy(field);
                setSortDir(dir);
                setPage(0);
              }}
              onReactivate={handleReactivate}
            />
          </>
        )}
      </Box>

      {/* Modal de confirmación de reactivación */}
      <ConfirmationModal
        open={!!pendingReactivateRow}
        handleClose={() => setPendingReactivateRow(null)}
        handleConfirm={handleConfirmReactivate}
        title="¿Reactivar cuestionario?"
        description={
          pendingReactivateRow
            ? `¿Desea reactivar el cuestionario "${pendingReactivateRow.questionnaireTitle}" para ${pendingReactivateRow.userName}?`
            : ""
        }
        confirmText="Sí, reactivar"
        cancelText="Cancelar"
        confirmColor="warning"
      />

      {error && <ErrorSnackbar open={errorOpen} onClose={() => setErrorOpen(false)} title="Ha ocurrido un error" message={error.message} />}

      <ErrorSnackbar
        open={reactivateErrorOpen}
        onClose={() => setReactivateErrorOpen(false)}
        title="Error al reactivar"
        message={reactivateErrorMessage}
      />
    </Box>
  );
};

export default ApplicationAssignmentsPage;
